import { Alert, Box, Chip, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../services/api";
import { useApiData } from "../hooks/useApiData";
import { useSession } from "../context/SessionContext";
import { DataState } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import DataTable, { TwoLine } from "../components/DataTable";
import StatusChip from "../components/StatusChip";
import ContractsDialog from "../components/ContractsDialog";
import AskContractsCard from "../components/AskContractsCard";
import { TIER } from "../constants";
import { formatDate } from "../utils/format";

// A vendor that handles PHI needs a signed Business Associate Agreement under HIPAA.
const BAA_COLOR = { SIGNED: "#059669", IN_NEGOTIATION: "#D97706", MISSING: "#DC2626", NOT_REQUIRED: "#94A3B8" };
const BAA_ORDER = { MISSING: 0, IN_NEGOTIATION: 1, SIGNED: 2, NOT_REQUIRED: 3 };

const FILTERS = [
  { value: "all", label: "All vendors", test: () => true },
  { value: "phi", label: "Handle PHI", test: (v) => v.handlesPhi },
  { value: "gap", label: "BAA gaps", test: (v) => v.baaGap },
  { value: "expiring", label: "BAA ending soon", test: (v) => v.baaExpiringSoon },
];

export default function VendorsPage() {
  const { clientId, isAll } = useSession();
  const [openVendor, setOpenVendor] = useState(null);
  const state = useApiData(() => api.getVendors(), [clientId]);
  const [params, setParams] = useSearchParams();
  const filter = FILTERS.some((f) => f.value === params.get("baa")) ? params.get("baa") : "all";

  const setFilter = (value) => {
    if (!value) return;
    const next = new URLSearchParams(params);
    if (value === "all") next.delete("baa");
    else next.set("baa", value);
    setParams(next, { replace: true });
  };

  const columns = [
    { key: "name", label: "Vendor", render: (v) => <TwoLine main={v.name} sub={v.service} /> },
    {
      key: "riskTier", label: "Risk tier", sortValue: (v) => (v.riskTier ? Number(v.riskTier.slice(-1)) : 99),
      exportValue: (v) => v.riskTierLabel,
      render: (v) => <StatusChip label={v.riskTierLabel} color={TIER[v.riskTier || "UNASSIGNED"].color} />,
    },
    {
      key: "hasAiComponents", label: "AI in product", exportValue: (v) => (v.hasAiComponents ? "Yes" : "No"),
      render: (v) => (v.hasAiComponents ? <Chip size="small" color="secondary" label="Uses AI" /> : "—"),
    },
    {
      key: "handlesPhi", label: "Handles PHI", sortValue: (v) => (v.handlesPhi ? 0 : 1),
      exportValue: (v) => (v.handlesPhi ? "Yes" : "No"),
      render: (v) => (v.handlesPhi ? <Chip size="small" variant="outlined" label="PHI" /> : "—"),
    },
    {
      key: "baaStatus", label: "BAA", sortValue: (v) => BAA_ORDER[v.baaStatus] ?? 9, exportValue: (v) => v.baaStatusLabel,
      render: (v) => <StatusChip label={v.baaStatusLabel} color={BAA_COLOR[v.baaStatus] || BAA_COLOR.NOT_REQUIRED} />,
    },
    {
      key: "baaExpiresOn", label: "BAA ends", nowrap: true, sortValue: (v) => v.baaExpiresOn || "9999",
      exportValue: (v) => v.baaExpiresOn || "",
      render: (v) =>
        v.baaExpiresOn ? (
          <Typography variant="body2" sx={{ color: v.baaExpiringSoon ? "#B45309" : "inherit", fontWeight: v.baaExpiringSoon ? 700 : 400 }}>
            {formatDate(v.baaExpiresOn)}
          </Typography>
        ) : "—",
    },
    { key: "lastAssessedOn", label: "Last assessed", sortValue: (v) => v.lastAssessedOn || "", exportValue: (v) => v.lastAssessedOn || "", render: (v) => formatDate(v.lastAssessedOn) },
    { key: "contractEnd", label: "Contract ends", sortValue: (v) => v.contractEnd || "", exportValue: (v) => v.contractEnd || "", render: (v) => formatDate(v.contractEnd) },
  ];

  return (
    <>
      <PageHeader title="Vendors" subtitle="Third parties we depend on, with a note where they use AI inside their product and whether they hold a HIPAA business associate agreement (BAA). Click a vendor to upload and review its contracts." />
      <DataState state={state}>
        {(rows) => {
          const gaps = rows.filter((v) => v.baaGap).length;
          const ending = rows.filter((v) => v.baaExpiringSoon).length;
          const shown = rows.filter((FILTERS.find((f) => f.value === filter) || FILTERS[0]).test);
          return (
            <>
              {(gaps > 0 || ending > 0) && (
                <Alert severity={gaps > 0 ? "error" : "warning"} sx={{ mb: 2 }}>
                  {gaps > 0 && `${gaps} ${gaps === 1 ? "vendor handles" : "vendors handle"} PHI without a signed BAA. `}
                  {ending > 0 && `${ending} signed ${ending === 1 ? "BAA ends" : "BAAs end"} within 90 days.`}
                </Alert>
              )}
              {isAll ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Contracts belong to one department. Pick a department at the top to upload contracts or ask questions about them.
                </Alert>
              ) : (
                <Box sx={{ mb: 3 }}><AskContractsCard /></Box>
              )}
              <SectionCard>
                <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e, v) => setFilter(v)} sx={{ mb: 2, flexWrap: "wrap" }} aria-label="Filter vendors">
                  {FILTERS.map((f) => (
                    <ToggleButton key={f.value} value={f.value} sx={{ textTransform: "none", px: 2 }}>{f.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <DataTable columns={columns} rows={shown} exportName="vendors" onRowClick={isAll ? undefined : setOpenVendor} defaultSort={{ key: "baaStatus", dir: "asc" }}
                  emptyText={filter === "all" ? "No vendors yet." : "No vendors match this filter."} />
              </SectionCard>
            </>
          );
        }}
      </DataState>
      {openVendor && <ContractsDialog vendor={openVendor} onClose={() => setOpenVendor(null)} />}
    </>
  );
}
